import React from "react";
import { PriceRange } from "./useStockPrices";
import { isoToPickerValue, pickerValueToIso } from "./types";

interface TimeRangeBarProps {
  range: PriceRange;
  slotCount: number;
  loading: boolean;
  onShift: (hours: number) => void;
  onRangeChange: (range: PriceRange) => void;
}

const STEP_HOURS = 24;

const TimeRangeBar: React.FC<TimeRangeBarProps> = ({
  range,
  slotCount,
  loading,
  onShift,
  onRangeChange,
}) => {
  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const iso = pickerValueToIso(e.target.value);
    if (iso) onRangeChange({ from: iso, to: range.to });
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const iso = pickerValueToIso(e.target.value);
    if (iso) onRangeChange({ from: range.from, to: iso });
  };

  const btnCls =
    "px-2 py-1 text-xs font-mono bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator " +
    "text-gray-700 dark:text-gray-300 rounded hover:border-gray-300 dark:hover:border-gray-600 transition-colors " +
    "disabled:opacity-40";

  const inputCls =
    "px-2 py-1 text-xs font-mono bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator " +
    "rounded text-gray-900 dark:text-white focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all";

  return (
    <div
      data-testid="time-range-bar"
      className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-rp-separator text-xs"
    >
      <button
        type="button"
        className={btnCls}
        disabled={loading}
        onClick={() => onShift(-STEP_HOURS)}
        aria-label="Shift range earlier"
      >
        -{STEP_HOURS}h
      </button>

      <label className="sr-only" htmlFor="range-from">From (UTC)</label>
      <input
        id="range-from"
        type="datetime-local"
        className={inputCls}
        value={isoToPickerValue(range.from)}
        onChange={handleFromChange}
        disabled={loading}
        aria-label="From (UTC)"
      />

      <span className="text-gray-400 dark:text-gray-500">to</span>

      <label className="sr-only" htmlFor="range-to">To (UTC)</label>
      <input
        id="range-to"
        type="datetime-local"
        className={inputCls}
        value={isoToPickerValue(range.to)}
        onChange={handleToChange}
        disabled={loading}
        aria-label="To (UTC)"
      />

      <button
        type="button"
        className={btnCls}
        disabled={loading}
        onClick={() => onShift(STEP_HOURS)}
        aria-label="Shift range later"
      >
        +{STEP_HOURS}h
      </button>

      <span className="ml-auto text-gray-500 dark:text-gray-400">
        {loading ? "Loading..." : `${slotCount} slot${slotCount !== 1 ? "s" : ""}`}
      </span>
    </div>
  );
};

export default TimeRangeBar;
